import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Calendar, Clock, MapPin } from "lucide-react";
import type { CalendarEvent } from "@shared/schema";

interface GoalCalendarTabProps {
  goalId: string;
}

export default function GoalCalendarTab({ goalId }: GoalCalendarTabProps) {
  const [, setLocation] = useLocation();

  const { data: events = [], isLoading } = useQuery<CalendarEvent[]>({
    queryKey: ["/api/calendar", goalId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (goalId) params.append("goalId", goalId);
      const response = await fetch(`/api/calendar?${params.toString()}`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error('Failed to fetch calendar events');
      return response.json();
    },
  });

  const handleCreateEvent = () => {
    // Navigate to calendar page where user can create a new event
    setLocation("/calendar");
  };

  const handleViewEvent = (eventId: string) => {
    // Navigate to calendar page with the specific event
    setLocation(`/calendar?eventId=${eventId}`);
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  const formatTime = (date: Date | string) => {
    return new Date(date).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit"
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Calendar Events</h2>
          <p className="text-sm text-muted-foreground">Events linked to this goal</p>
        </div>
        <Button onClick={handleCreateEvent} data-testid="button-create-event">
          <Plus className="w-4 h-4 mr-2" />
          New Event
        </Button>
      </div>

      {isLoading ? (
        <div className="text-center py-8">
          <div className="w-6 h-6 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-muted-foreground">Loading events...</p>
        </div>
      ) : events.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground mb-4">No calendar events linked to this goal yet</p>
            <Button 
              variant="outline" 
              onClick={handleCreateEvent}
              data-testid="button-create-first-event"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Event
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {events.map((event) => (
            <Card 
              key={event.id} 
              data-testid={`card-event-${event.id}`}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => handleViewEvent(event.id)}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{event.title}</CardTitle>
                    <div className="flex items-center gap-3 mt-2 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(event.startTime)}
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatTime(event.startTime)}
                        {event.endTime && ` - ${formatTime(event.endTime)}`}
                      </div>
                    </div>
                  </div>
                  {event.completedAt && (
                    <Badge className="bg-green-500/10 text-green-700 dark:text-green-300">
                      Completed
                    </Badge>
                  )}
                </div>
              </CardHeader>
              {(event.description || event.location) && (
                <CardContent className="space-y-2">
                  {event.description && (
                    <p className="text-sm text-muted-foreground">{event.description}</p>
                  )}
                  {event.location && (
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <MapPin className="w-4 h-4" />
                      {event.location}
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
